import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Plus, Pencil, Check, X, Wand2 } from "lucide-react";

// Sensible default codes for commonly used immigration application types.
// Matching is case-insensitive on the normalized name.
const DEFAULT_CODES = {
  "study permit": "SP",
  "study permit extension": "SPE",
  "permanent residence": "PR",
  "express entry": "EE",
  "pnp": "PNP",
  "lmia": "LMIA",
  "lmia renewal": "LMIAR",
  "super visa": "SV",
  "visitor record": "VR",
  "pgwp": "PGWP",
  "spouse open work permit": "SOWP",
  "citizenship": "CIT",
  "restoration": "RST",
  "other": "OTH",
};

export default function ImmigrationAppTypes() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editCode, setEditCode] = useState("");
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    const r = await api.get("/immigration/app-types?include_inactive=true");
    setRows(r.data);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) return toast.error("Name required");
    try {
      await api.post("/immigration/app-types", { name: name.trim(), code: code.trim() });
      toast.success("Added");
      setName(""); setCode(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggle = async (r) => {
    try {
      await api.patch(`/immigration/app-types/${r.id}`, { is_active: !r.is_active });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setEditCode(r.code || (DEFAULT_CODES[(r.name || "").toLowerCase()] || ""));
  };
  const cancelEdit = () => { setEditingId(null); setEditCode(""); };
  const saveEdit = async (r) => {
    const v = (editCode || "").trim().toUpperCase();
    if (!v) return toast.error("Code required");
    try {
      await api.patch(`/immigration/app-types/${r.id}`, { code: v });
      toast.success("Code updated");
      cancelEdit();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const seedDefaults = async () => {
    const missing = rows.filter(r => !r.code);
    const toSeed = missing
      .map(r => ({ r, code: DEFAULT_CODES[(r.name || "").toLowerCase()] }))
      .filter(x => !!x.code);
    if (toSeed.length === 0) {
      toast.message("Nothing to seed", { description: "All types already have codes or no defaults match." });
      return;
    }
    // Avoid colliding with codes already in use on other rows.
    const used = new Set(rows.filter(r => r.code).map(r => r.code.toUpperCase()));
    setSeeding(true);
    let ok = 0, fail = 0, skipped = 0;
    for (const { r, code: c } of toSeed) {
      if (used.has(c.toUpperCase())) { skipped++; continue; }
      try {
        await api.patch(`/immigration/app-types/${r.id}`, { code: c });
        used.add(c.toUpperCase());
        ok++;
      } catch { fail++; }
    }
    setSeeding(false);
    await load();
    toast.success(`Seeded ${ok} codes${skipped?` · skipped ${skipped} duplicate`:""}${fail?` · ${fail} failed`:""}`);
  };

  const missingCount = rows.filter(r => !r.code).length;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="heading-font text-3xl font-bold text-slate-900">Immigration Application Types</h1>
          <p className="text-sm text-slate-500 mt-1">Manage the dropdown available on new immigration files. Codes are used to generate case IDs like <span className="mono text-xs">SP-JS-0012</span>.</p>
        </div>
        {missingCount > 0 && (
          <Button
            data-testid="imm-type-seed-defaults"
            onClick={seedDefaults}
            disabled={seeding}
            variant="outline"
            className="border-orange-300 text-orange-700 hover:bg-orange-50"
          >
            <Wand2 className="w-4 h-4 mr-1"/>
            {seeding ? "Seeding…" : `Auto-fill ${missingCount} missing code${missingCount>1?"s":""}`}
          </Button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs text-slate-600">Name</Label>
          <Input data-testid="imm-type-name" value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. LMIA Renewal" />
        </div>
        <div className="w-32">
          <Label className="text-xs text-slate-600">Code *</Label>
          <Input data-testid="imm-type-code" value={code} onChange={e=>setCode(e.target.value.toUpperCase())} placeholder="SP, PR, SV, LMIA" maxLength={10} required />
        </div>
        <Button data-testid="imm-type-add" onClick={add} className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-1"/>Add</Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Code</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-right px-4 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r=>(
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-4 py-2 font-medium">{r.name}</td>
                <td className="px-4 py-2">
                  {editingId === r.id ? (
                    <div className="flex items-center gap-2">
                      <Input
                        data-testid={`imm-type-edit-code-${r.id}`}
                        autoFocus
                        value={editCode}
                        onChange={e=>setEditCode(e.target.value.toUpperCase())}
                        onKeyDown={e=>{ if(e.key==='Enter') saveEdit(r); if(e.key==='Escape') cancelEdit(); }}
                        className="h-8 w-28 mono text-xs"
                        maxLength={10}
                      />
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" onClick={()=>saveEdit(r)} data-testid={`imm-type-save-${r.id}`}><Check className="w-4 h-4"/></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500" onClick={cancelEdit}><X className="w-4 h-4"/></Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={()=>startEdit(r)}
                      data-testid={`imm-type-edit-${r.id}`}
                      className="group inline-flex items-center gap-2 mono text-xs text-slate-700 hover:text-orange-600"
                      title="Click to edit code"
                    >
                      <span className={r.code ? "" : "text-slate-400"}>{r.code || "—"}</span>
                      <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity"/>
                    </button>
                  )}
                </td>
                <td className="px-4 py-2">{r.is_active ? <span className="text-emerald-600">Active</span> : <span className="text-slate-400">Inactive</span>}</td>
                <td className="px-4 py-2 text-right">
                  <Button variant="outline" size="sm" onClick={()=>toggle(r)}>{r.is_active?"Deactivate":"Activate"}</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
